// frontend/src/utils/crmApi.js
import axios from 'axios';

// Normalize API URL (remove trailing slash to avoid double slashes)
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');

// Create axios instance for CRM API
const crmApi = axios.create({
    baseURL: `${API_URL}/api/crm`,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Public API (no auth)
const publicApi = axios.create({
    baseURL: `${API_URL}/public`,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request interceptor to add token
crmApi.interceptors.request.use(
    (config) => {
        // Only log in development and only for non-polling requests (reduce noise)
        const isPollingRequest = config.url?.includes('/messages/conversations') || 
                                 config.url?.includes('/messages/by-conversation');
        const shouldLog = process.env.NODE_ENV === 'development' && !isPollingRequest && Math.random() < 0.1; // 10% chance for non-polling
        
        if (shouldLog) {
            console.log('🚀 API Request:', {
                url: config.url,
                method: config.method
            });
        }
        
        const token = localStorage.getItem('crm_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        } else if (process.env.NODE_ENV === 'development' && !isPollingRequest) {
            console.warn('⚠️ No token found in localStorage');
        }
        
        const agencyId = localStorage.getItem('current_agency_id');
        if (agencyId) {
            config.headers['X-Agency-Id'] = agencyId;
        } else if (process.env.NODE_ENV === 'development' && !isPollingRequest) {
            console.warn('⚠️ No agency ID found in localStorage');
        }
        
        return config;
    },
    (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
crmApi.interceptors.response.use(
    (response) => {
        // Only log in development and only for non-polling requests (reduce noise)
        const isPollingRequest = response.config.url?.includes('/messages/conversations') || 
                                 response.config.url?.includes('/messages/by-conversation');
        const shouldLog = process.env.NODE_ENV === 'development' && !isPollingRequest && Math.random() < 0.1; // 10% chance for non-polling
        
        if (shouldLog) {
            console.log('✅ API Response:', {
                url: response.config.url,
                status: response.status
            });
        }
        return response;
    },
    (error) => {
        // Don't log 404 errors as they're often expected (e.g., checking if a contact exists)
        if (error.response?.status !== 404) {
            console.error('❌ API Error:', {
                url: error.config?.url,
                status: error.response?.status,
                message: error.message,
                response: error.response?.data
            });
        }
        
        if (error.response?.status === 401) {
            // Only redirect if this is NOT a login attempt
            const isLoginAttempt = error.config?.url?.includes('/auth/login');
            const isSignupAttempt = error.config?.url?.includes('/auth/signup');
            const isResetPasswordAttempt = error.config?.url?.includes('/auth/reset-password');
            const isForgotPasswordAttempt = error.config?.url?.includes('/auth/forgot-password');
            const isGetMeAttempt = error.config?.url?.includes('/auth/me');
            
            // Don't redirect if already on login page or during auth operations
            const currentPath = window.location.pathname;
            // Check for auth pages - handle both with and without /crm prefix (for basename compatibility)
            const pathWithoutCrm = currentPath.replace(/^\/crm/, '');
            const isOnAuthPage = currentPath === '/login' || currentPath === '/signup' || currentPath === '/verify-email' ||
                                  currentPath.startsWith('/reset-password') ||
                                  pathWithoutCrm === '/login' || pathWithoutCrm === '/signup' || pathWithoutCrm === '/verify-email' ||
                                  pathWithoutCrm.startsWith('/reset-password');
            
            // Check if error message indicates a real auth failure
            const errorMessage = error.response?.data?.message || '';
            const isAuthError = errorMessage.includes('token') || 
                               errorMessage.includes('authentication') || 
                               errorMessage.includes('unauthorized') ||
                               errorMessage.includes('expired') ||
                               errorMessage.includes('invalid') ||
                               errorMessage.includes('User not found') ||
                               errorMessage.includes('Email verification required');
            
            // Only clear token and redirect if it's a real auth error AND not during auth operations
            if (isAuthError && 
                !isLoginAttempt && 
                !isSignupAttempt && 
                !isResetPasswordAttempt && 
                !isForgotPasswordAttempt && 
                !isOnAuthPage) {
                
                // For /auth/me errors, be more conservative - might be temporary backend issue
                if (isGetMeAttempt && !errorMessage.includes('token') && !errorMessage.includes('expired')) {
                    console.warn('⚠️ /auth/me returned 401, but error message suggests temporary issue. Not clearing token.');
                    return Promise.reject(error);
                }
                
                console.warn('⚠️ Authentication error detected, clearing token and redirecting:', errorMessage);
                localStorage.removeItem('crm_token');
                localStorage.removeItem('current_agency_id');
                // Use navigate instead of window.location to avoid full page reload and loop
                // But since we're in an interceptor, we need to use window.location
                // However, check if we're already on login to prevent loop
                // Simple path - basename handles /crm prefix automatically
                const pathWithoutCrm = currentPath.replace(/^\/crm/, '');
                if (pathWithoutCrm !== '/login' && currentPath !== '/login') {
                    window.location.href = '/login';
                }
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    signup: (data) => crmApi.post('/auth/signup', data),
    verifyEmail: (data) => crmApi.post('/auth/verify-email', data),
    login: (data) => crmApi.post('/auth/login', data),
    forgotPassword: (data) => crmApi.post('/auth/forgot-password', data),
    resetPassword: (data) => crmApi.post('/auth/reset-password', data),
    getMe: () => crmApi.get('/auth/me'),
    updateMe: (data) => crmApi.put('/auth/me', data),
    changePassword: (data) => crmApi.post('/auth/change-password', data),
    switchAgency: (agencyId) => crmApi.post('/auth/switch-agency', { agencyId })
};

// Agency API
export const agencyAPI = {
    getAll: () => crmApi.get('/agencies'),
    getById: (id) => crmApi.get(`/agencies/${id}`),
    create: (data) => crmApi.post('/agencies', data),
    getMembers: (id) => crmApi.get(`/agencies/${id}/members`),
    update: (id, data) => crmApi.put(`/agencies/${id}`, data),
    delete: (id) => crmApi.delete(`/agencies/${id}`),
    getStats: (id) => crmApi.get(`/agencies/${id}/stats`),
    verifyDomain: (id) => crmApi.patch(`/agencies/${id}/domains/verify`)
};

// Users API
export const usersAPI = {
    getAll: (params) => crmApi.get('/users/agents', { params }),
    getAgents: () => crmApi.get('/users/agents')
};

// Client API
export const clientAPI = {
    getAll: (params) => crmApi.get('/clients', { params }),
    getById: (id) => crmApi.get(`/clients/${id}`),
    create: (data) => crmApi.post('/clients', data),
    update: (id, data) => crmApi.put(`/clients/${id}`, data),
    delete: (id) => crmApi.delete(`/clients/${id}`),
    deleteMultiple: (ids) => crmApi.post('/clients/delete-multiple', { ids }),
    invite: (id) => crmApi.post(`/clients/${id}/invite`),
    getTasks: (id) => crmApi.get(`/clients/${id}/tasks`),
    getEmailTemplates: () => crmApi.get('/clients/email-templates'),
    sendEmail: (data) => crmApi.post('/clients/send-email', data)
};

// Account API
export const accountAPI = {
    getAll: (params) => crmApi.get('/accounts', { params }),
    getById: (id) => crmApi.get(`/accounts/${id}`),
    create: (data) => crmApi.post('/accounts', data),
    update: (id, data) => crmApi.put(`/accounts/${id}`, data),
    delete: (id) => crmApi.delete(`/accounts/${id}`),
    deleteMultiple: (ids) => crmApi.post('/accounts/delete-multiple', { ids }),
    getClients: (id) => crmApi.get(`/accounts/${id}/clients`)
};

export const leadAPI = {
    getAll: (params) => crmApi.get('/leads', { params }),
    getById: (id) => crmApi.get(`/leads/${id}`),
    create: (data) => crmApi.post('/leads', data),
    update: (id, data) => crmApi.put(`/leads/${id}`, data),
    delete: (id) => crmApi.delete(`/leads/${id}`),
    convert: (id, data) => crmApi.post(`/leads/${id}/convert`, data),
    updateEngagement: (id, type, data) => crmApi.post(`/leads/${id}/update-engagement`, { type, data }),
    getStats: () => crmApi.get('/leads/stats/summary')
};

export const contactAPI = {
    getAll: (params) => crmApi.get('/contacts', { params }),
    getById: (id) => crmApi.get(`/contacts/${id}`),
    create: (data) => crmApi.post('/contacts', data),
    update: (id, data) => crmApi.put(`/contacts/${id}`, data),
    delete: (id) => crmApi.delete(`/contacts/${id}`)
};

export const emailCampaignAPI = {
    getAll: (params) => crmApi.get('/email-campaigns', { params }),
    getById: (id) => crmApi.get(`/email-campaigns/${id}`),
    create: (data) => crmApi.post('/email-campaigns', data),
    update: (id, data) => crmApi.put(`/email-campaigns/${id}`, data),
    delete: (id) => crmApi.delete(`/email-campaigns/${id}`),
    updateStatus: (id, status) => crmApi.post(`/email-campaigns/${id}/status`, { status }),
    bulkAction: (campaignIds, action) => crmApi.post('/email-campaigns/bulk-action', { campaignIds, action }),
    getStats: () => crmApi.get('/email-campaigns/stats'),
    preview: (id) => crmApi.get(`/email-campaigns/${id}/preview`),
    testSend: (id, testEmails) => crmApi.post(`/email-campaigns/${id}/test`, { testEmails }),
    duplicate: (id) => crmApi.post(`/email-campaigns/${id}/duplicate`)
};

// Enhanced Campaigns API
export const campaignsAPI = {
    getAll: (params) => crmApi.get('/campaigns', { params }),
    getById: (id) => crmApi.get(`/campaigns/${id}`),
    create: (data) => crmApi.post('/campaigns', data),
    update: (id, data) => crmApi.patch(`/campaigns/${id}`, data),
    delete: (id) => crmApi.delete(`/campaigns/${id}`),
    duplicate: (id) => crmApi.post(`/campaigns/${id}/duplicate`),
    start: (id) => crmApi.post(`/campaigns/${id}/start`),
    schedule: (id, data) => crmApi.post(`/campaigns/${id}/schedule`, data),
    pause: (id) => crmApi.post(`/campaigns/${id}/pause`),
    resume: (id) => crmApi.post(`/campaigns/${id}/resume`),
    testSend: (id, data) => crmApi.post(`/campaigns/${id}/test-send`, data),
    getAnalytics: (id, params) => crmApi.get(`/campaigns/${id}/analytics`, { params })
};

// Audiences API
export const audiencesAPI = {
    getAll: (params) => crmApi.get('/audiences', { params }),
    getById: (id) => crmApi.get(`/audiences/${id}`),
    create: (data) => crmApi.post('/audiences', data),
    update: (id, data) => crmApi.patch(`/audiences/${id}`, data),
    delete: (id) => crmApi.delete(`/audiences/${id}`),
    calculateSize: (id) => crmApi.post(`/audiences/${id}/calculate`)
};

// Sending Profiles API
export const sendingProfilesAPI = {
    getAll: (params) => crmApi.get('/sending-profiles', { params }),
    getById: (id) => crmApi.get(`/sending-profiles/${id}`),
    create: (data) => crmApi.post('/sending-profiles', data),
    update: (id, data) => crmApi.patch(`/sending-profiles/${id}`, data),
    delete: (id) => crmApi.delete(`/sending-profiles/${id}`),
    verify: (id, data) => crmApi.post(`/sending-profiles/${id}/verify`, data)
};

// Email Templates API
export const emailTemplateAPI = {
    getAll: (params) => crmApi.get('/email-templates', { params }),
    getById: (id) => crmApi.get(`/email-templates/${id}`),
    create: (data) => crmApi.post('/email-templates', data),
    update: (id, data) => crmApi.put(`/email-templates/${id}`, data),
    delete: (id) => crmApi.delete(`/email-templates/${id}`),
    duplicate: (id) => crmApi.post(`/email-templates/${id}/duplicate`)
};

export const workflowAPI = {
    getAll: (params) => crmApi.get('/workflows', { params }),
    getById: (id) => crmApi.get(`/workflows/${id}`),
    create: (data) => crmApi.post('/workflows', data),
    update: (id, data) => crmApi.put(`/workflows/${id}`, data),
    delete: (id) => crmApi.delete(`/workflows/${id}`),
    updateStatus: (id, status) => crmApi.post(`/workflows/${id}/status`, { status }),
    bulkAction: (workflowIds, action) => crmApi.post('/workflows/bulk-action', { workflowIds, action }),
    getStats: () => crmApi.get('/workflows/stats'),
    execute: (id, data) => crmApi.post(`/workflows/${id}/execute`, data),
    test: (id, data) => crmApi.post(`/workflows/${id}/test`, data),
    duplicate: (id) => crmApi.post(`/workflows/${id}/duplicate`),
    getTemplates: () => crmApi.get('/workflows/templates'),
    getExecutionLogs: (id, params) => crmApi.get(`/workflows/${id}/logs`, { params })
};

export const activityAPI = {
    getAll: (params) => crmApi.get('/activities', { params }),
    getById: (id) => crmApi.get(`/activities/${id}`),
    create: (data) => crmApi.post('/activities', data),
    update: (id, data) => crmApi.put(`/activities/${id}`, data),
    delete: (id) => crmApi.delete(`/activities/${id}`),
    getUpcoming: (days = 7) => crmApi.get('/activities/upcoming', { params: { days } }),
    getOverdue: () => crmApi.get('/activities/overdue'),
    complete: (id) => crmApi.post(`/activities/${id}/complete`),
    reschedule: (id, scheduledDate) => crmApi.post(`/activities/${id}/reschedule`, { scheduledDate }),
    getForEntity: (entityType, entityId, params) => crmApi.get('/activities', { 
        params: { ...params, entityType, entityId } 
    })
};

export const documentAPI = {
    getAll: (params) => crmApi.get('/documents', { params }),
    getById: (id) => crmApi.get(`/documents/${id}`),
    create: (data) => crmApi.post('/documents', data),
    update: (id, data) => crmApi.put(`/documents/${id}`, data),
    delete: (id) => crmApi.delete(`/documents/${id}`),
    upload: (formData) => crmApi.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    download: (id) => crmApi.get(`/documents/${id}/download`, { responseType: 'blob' }),
    bulkAction: (documentIds, action) => crmApi.post('/documents/bulk-action', { documentIds, action }),
    getStats: () => crmApi.get('/documents/stats'),
    share: (id, data) => crmApi.post(`/documents/${id}/share`, data),
    getTemplates: () => crmApi.get('/documents/templates'),
    generateFromTemplate: (templateId, data) => crmApi.post(`/documents/templates/${templateId}/generate`, data),
    requestSignature: (id, data) => crmApi.post(`/documents/${id}/signature-request`, data),
    getSignatureStatus: (id) => crmApi.get(`/documents/${id}/signature-status`),
    archive: (id) => crmApi.post(`/documents/${id}/archive`),
    restore: (id) => crmApi.post(`/documents/${id}/restore`),
    getVersions: (id) => crmApi.get(`/documents/${id}/versions`),
    createVersion: (id, data) => crmApi.post(`/documents/${id}/versions`, data)
};

export const locationAPI = {
    getAll: (params) => crmApi.get('/locations', { params }),
    getById: (id) => crmApi.get(`/locations/${id}`),
    create: (data) => crmApi.post('/locations', data),
    update: (id, data) => crmApi.put(`/locations/${id}`, data),
    delete: (id) => crmApi.delete(`/locations/${id}`),
    bulkAction: (locationIds, action) => crmApi.post('/locations/bulk-action', { locationIds, action }),
    getStats: () => crmApi.get('/locations/stats'),
    getInsights: (id) => crmApi.get(`/locations/${id}/insights`),
    getMarketData: (id) => crmApi.get(`/locations/${id}/market-data`),
    getCostAnalysis: (id) => crmApi.get(`/locations/${id}/cost-analysis`),
    getDemographics: (id) => crmApi.get(`/locations/${id}/demographics`),
    getHousingData: (id) => crmApi.get(`/locations/${id}/housing`),
    getEducationData: (id) => crmApi.get(`/locations/${id}/education`),
    getHealthcareData: (id) => crmApi.get(`/locations/${id}/healthcare`),
    getTransportationData: (id) => crmApi.get(`/locations/${id}/transportation`),
    getSafetyData: (id) => crmApi.get(`/locations/${id}/safety`),
    getClimateData: (id) => crmApi.get(`/locations/${id}/climate`),
    getVisaRequirements: (id) => crmApi.get(`/locations/${id}/visa-requirements`),
    getTaxInformation: (id) => crmApi.get(`/locations/${id}/tax-info`),
    compareLocations: (locationIds) => crmApi.post('/locations/compare', { locationIds }),
    exportData: (locationIds) => crmApi.post('/locations/export', { locationIds })
};

export const billingAPI = {
    getAll: (params) => crmApi.get('/billing', { params }),
    getById: (id) => crmApi.get(`/billing/${id}`),
    create: (data) => crmApi.post('/billing', data),
    update: (id, data) => crmApi.put(`/billing/${id}`, data),
    delete: (id) => crmApi.delete(`/billing/${id}`),
    updateStatus: (id, status) => crmApi.post(`/billing/${id}/status`, { status }),
    bulkAction: (invoiceIds, action) => crmApi.post('/billing/bulk-action', { invoiceIds, action }),
    getStats: () => crmApi.get('/billing/stats'),
    sendInvoice: (id) => crmApi.post(`/billing/${id}/send`),
    download: (id) => crmApi.get(`/billing/${id}/download`, { responseType: 'blob' }),
    print: (id) => crmApi.get(`/billing/${id}/print`),
    duplicate: (id) => crmApi.post(`/billing/${id}/duplicate`),
    getTemplates: () => crmApi.get('/billing/templates'),
    createFromTemplate: (templateId, data) => crmApi.post(`/billing/templates/${templateId}/create`, data),
    recordPayment: (id, data) => crmApi.post(`/billing/${id}/payment`, data),
    refund: (id, data) => crmApi.post(`/billing/${id}/refund`, data),
    getPaymentHistory: (id) => crmApi.get(`/billing/${id}/payments`),
    getReports: (params) => crmApi.get('/billing/reports', { params }),
    exportData: (params) => crmApi.post('/billing/export', params),
    getRecurringInvoices: () => crmApi.get('/billing/recurring'),
    createRecurring: (data) => crmApi.post('/billing/recurring', data),
    updateRecurring: (id, data) => crmApi.put(`/billing/recurring/${id}`, data),
    deleteRecurring: (id) => crmApi.delete(`/billing/recurring/${id}`)
};

export const analyticsAPI = {
    getAll: (params) => crmApi.get('/analytics', { params }),
    getById: (id) => crmApi.get(`/analytics/${id}`),
    create: (data) => crmApi.post('/analytics', data),
    update: (id, data) => crmApi.put(`/analytics/${id}`, data),
    delete: (id) => crmApi.delete(`/analytics/${id}`),
    bulkAction: (reportIds, action) => crmApi.post('/analytics/bulk-action', { reportIds, action }),
    getDashboard: () => crmApi.get('/analytics/dashboard'),
    generate: (id) => crmApi.post(`/analytics/${id}/generate`),
    download: (id) => crmApi.get(`/analytics/${id}/download`, { responseType: 'blob' }),
    schedule: (id, data) => crmApi.post(`/analytics/${id}/schedule`, data),
    getTemplates: () => crmApi.get('/analytics/templates'),
    createFromTemplate: (templateId, data) => crmApi.post(`/analytics/templates/${templateId}/create`, data),
    getKPIs: (params) => crmApi.get('/analytics/kpis', { params }),
    getMetrics: (params) => crmApi.get('/analytics/metrics', { params }),
    getCharts: (params) => crmApi.get('/analytics/charts', { params }),
    getInsights: (params) => crmApi.get('/analytics/insights', { params }),
    getPredictions: (params) => crmApi.get('/analytics/predictions', { params }),
    getComparisons: (params) => crmApi.get('/analytics/comparisons', { params }),
    exportData: (params) => crmApi.post('/analytics/export', params),
    getRealTimeData: () => crmApi.get('/analytics/real-time'),
    getCustomReports: () => crmApi.get('/analytics/custom-reports'),
    createCustomReport: (data) => crmApi.post('/analytics/custom-reports', data),
    updateCustomReport: (id, data) => crmApi.put(`/analytics/custom-reports/${id}`, data),
    deleteCustomReport: (id) => crmApi.delete(`/analytics/custom-reports/${id}`),
    getTrends: (params) => crmApi.get('/analytics/trends', { params })
};

export const systemAPI = {
    getConfig: () => crmApi.get('/system/config'),
    getTemplates: (params) => crmApi.get('/system/templates', { params }),
    getAnnouncements: (params) => crmApi.get('/system/announcements', { params })
};

export const adminSystemAPI = {
    getConfig: () => crmApi.get('/admin/system-config'),
    updateConfig: (data) => crmApi.patch('/admin/system-config', data),
    getTemplates: (params) => crmApi.get('/admin/library/templates', { params }),
    createTemplate: (data) => crmApi.post('/admin/library/templates', data),
    updateTemplate: (id, data) => crmApi.put(`/admin/library/templates/${id}`, data),
    deleteTemplate: (id) => crmApi.delete(`/admin/library/templates/${id}`),
    publishTemplate: (id, data) => crmApi.post(`/admin/library/templates/${id}/publish`, data),
    seedDefaultTemplates: (overwrite = false) => crmApi.post('/admin/library/templates/seed-defaults', { overwrite }),
    exportTemplate: (id) => crmApi.get(`/admin/library/templates/${id}/export`, { responseType: 'json' }),
    exportTemplates: (params) => crmApi.get('/admin/library/templates/export', { params, responseType: 'json' }),
    importTemplates: (templates, overwrite = false) => crmApi.post('/admin/library/templates/import', { templates, overwrite }),
    getAnnouncements: (params) => crmApi.get('/admin/announcements', { params }),
    createAnnouncement: (data) => crmApi.post('/admin/announcements', data),
    deleteAnnouncement: (id) => crmApi.delete(`/admin/announcements/${id}`)
};

export const clientPortalAPI = {
    getProfile: (clientId = null) => {
        const url = clientId ? `/client-portal/profile/${clientId}` : '/client-portal/profile';
        return crmApi.get(url);
    },
    getClientsList: () => crmApi.get('/client-portal/clients'),
    updateProfile: (data) => crmApi.put('/client-portal/profile', data),
    getNotifications: (clientId = null) => {
        const url = clientId ? `/client-portal/notifications?clientId=${clientId}` : '/client-portal/notifications';
        return crmApi.get(url);
    },
    markNotificationRead: (id) => crmApi.post(`/client-portal/notifications/${id}/read`),
    getDocuments: (clientId = null) => {
        const url = clientId ? `/client-portal/documents?clientId=${clientId}` : '/client-portal/documents';
        return crmApi.get(url);
    },
    downloadDocument: (id) => crmApi.get(`/client-portal/documents/${id}/download`, { responseType: 'blob' }),
    uploadDocument: (formData) => crmApi.post('/client-portal/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getTasks: (clientId = null) => {
        const url = clientId ? `/client-portal/tasks?clientId=${clientId}` : '/client-portal/tasks';
        return crmApi.get(url);
    },
    updateTask: (id, data) => crmApi.put(`/client-portal/tasks/${id}`, data),
    getAppointments: (clientId = null) => {
        const url = clientId ? `/client-portal/appointments?clientId=${clientId}` : '/client-portal/appointments';
        return crmApi.get(url);
    },
    bookAppointment: (data) => crmApi.post('/client-portal/appointments', data),
    rescheduleAppointment: (id, data) => crmApi.put(`/client-portal/appointments/${id}`, data),
    cancelAppointment: (id) => crmApi.delete(`/client-portal/appointments/${id}`),
    getMessages: (clientId = null) => {
        const url = clientId ? `/client-portal/messages?clientId=${clientId}` : '/client-portal/messages';
        return crmApi.get(url);
    },
    sendMessage: (data) => crmApi.post('/client-portal/messages', data),
    getBilling: (clientId = null) => {
        const url = clientId ? `/client-portal/billing?clientId=${clientId}` : '/client-portal/billing';
        return crmApi.get(url);
    },
    downloadInvoice: (id) => crmApi.get(`/client-portal/billing/${id}/download`, { responseType: 'blob' }),
    makePayment: (data) => crmApi.post('/client-portal/billing/payment', data),
    getPaymentHistory: () => crmApi.get('/client-portal/billing/payments'),
    getSettings: () => crmApi.get('/client-portal/settings'),
    updateSettings: (data) => crmApi.put('/client-portal/settings', data),
    getSupportTickets: () => crmApi.get('/client-portal/support'),
    createSupportTicket: (data) => crmApi.post('/client-portal/support', data),
    getSupportTicket: (id) => crmApi.get(`/client-portal/support/${id}`),
    updateSupportTicket: (id, data) => crmApi.put(`/client-portal/support/${id}`, data),
    getProgress: () => crmApi.get('/client-portal/progress'),
    getTimeline: () => crmApi.get('/client-portal/timeline'),
    getChecklist: () => crmApi.get('/client-portal/checklist'),
    updateChecklistItem: (id, data) => crmApi.put(`/client-portal/checklist/${id}`, data),
    getResources: () => crmApi.get('/client-portal/resources'),
    getFAQ: () => crmApi.get('/client-portal/faq'),
    getAnnouncements: () => crmApi.get('/client-portal/announcements'),
    markAnnouncementRead: (id) => crmApi.post(`/client-portal/announcements/${id}/read`)
};

export const taskAutomationAPI = {
    getAll: (params) => crmApi.get('/task-automation', { params }),
    getById: (id) => crmApi.get(`/task-automation/${id}`),
    create: (data) => crmApi.post('/task-automation', data),
    update: (id, data) => crmApi.put(`/task-automation/${id}`, data),
    delete: (id) => crmApi.delete(`/task-automation/${id}`),
    updateStatus: (id, status) => crmApi.post(`/task-automation/${id}/status`, { status }),
    bulkAction: (automationIds, action) => crmApi.post('/task-automation/bulk-action', { automationIds, action }),
    getStats: () => crmApi.get('/task-automation/stats'),
    test: (id) => crmApi.post(`/task-automation/${id}/test`),
    getLogs: (id) => crmApi.get(`/task-automation/${id}/logs`),
    getTemplates: () => crmApi.get('/task-automation/templates'),
    createFromTemplate: (templateId, data) => crmApi.post(`/task-automation/templates/${templateId}/create`, data),
    getAIInsights: () => crmApi.get('/task-automation/ai-insights'),
    getAISuggestions: (data) => crmApi.post('/task-automation/ai-suggestions', data),
    optimizeAutomation: (id) => crmApi.post(`/task-automation/${id}/optimize`),
    getPerformanceMetrics: (id) => crmApi.get(`/task-automation/${id}/metrics`),
    scheduleAutomation: (id, data) => crmApi.post(`/task-automation/${id}/schedule`, data),
    pauseAutomation: (id) => crmApi.post(`/task-automation/${id}/pause`),
    resumeAutomation: (id) => crmApi.post(`/task-automation/${id}/resume`),
    stopAutomation: (id) => crmApi.post(`/task-automation/${id}/stop`),
    duplicateAutomation: (id) => crmApi.post(`/task-automation/${id}/duplicate`),
    exportAutomation: (id) => crmApi.get(`/task-automation/${id}/export`),
    importAutomation: (data) => crmApi.post('/task-automation/import', data),
    getExecutionHistory: (id) => crmApi.get(`/task-automation/${id}/executions`),
    getErrorLogs: (id) => crmApi.get(`/task-automation/${id}/errors`),
    retryExecution: (id, executionId) => crmApi.post(`/task-automation/${id}/executions/${executionId}/retry`),
    getIntegrationStatus: () => crmApi.get('/task-automation/integrations'),
    testIntegration: (integrationId) => crmApi.post(`/task-automation/integrations/${integrationId}/test`),
    getAITrainingData: () => crmApi.get('/task-automation/ai/training-data'),
    trainAIModel: (data) => crmApi.post('/task-automation/ai/train', data),
    getAIPredictions: (data) => crmApi.post('/task-automation/ai/predict', data)
};

export const languageAPI = {
    getAll: (params) => crmApi.get('/languages', { params }),
    getById: (id) => crmApi.get(`/languages/${id}`),
    create: (data) => crmApi.post('/languages', data),
    update: (id, data) => crmApi.put(`/languages/${id}`, data),
    delete: (id) => crmApi.delete(`/languages/${id}`),
    updateStatus: (id, status) => crmApi.post(`/languages/${id}/status`, { status }),
    bulkAction: (languageIds, action) => crmApi.post('/languages/bulk-action', { languageIds, action }),
    getStats: () => crmApi.get('/languages/stats'),
    getTranslations: (params) => crmApi.get('/languages/translations', { params }),
    getTranslationKeys: () => crmApi.get('/languages/translation-keys'),
    updateTranslation: (id, data) => crmApi.put(`/languages/translations/${id}`, data),
    autoTranslate: (languageId) => crmApi.post(`/languages/${languageId}/auto-translate`),
    exportTranslations: (languageId) => crmApi.get(`/languages/${languageId}/export`, { responseType: 'blob' }),
    importTranslations: (languageId, formData) => crmApi.post(`/languages/${languageId}/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getTranslationStatus: (languageId) => crmApi.get(`/languages/${languageId}/translation-status`),
    getMissingTranslations: (languageId) => crmApi.get(`/languages/${languageId}/missing-translations`),
    getTranslationHistory: (languageId) => crmApi.get(`/languages/${languageId}/translation-history`),
    approveTranslation: (id) => crmApi.post(`/languages/translations/${id}/approve`),
    rejectTranslation: (id, reason) => crmApi.post(`/languages/translations/${id}/reject`, { reason }),
    getLanguageSettings: () => crmApi.get('/languages/settings'),
    updateLanguageSettings: (data) => crmApi.put('/languages/settings', data),
    getSupportedLanguages: () => crmApi.get('/languages/supported'),
    detectLanguage: (text) => crmApi.post('/languages/detect', { text }),
    getTranslationQuality: (languageId) => crmApi.get(`/languages/${languageId}/quality`),
    getTranslationMetrics: (languageId) => crmApi.get(`/languages/${languageId}/metrics`),
    getTranslationProviders: () => crmApi.get('/languages/providers'),
    testTranslationProvider: (providerId) => crmApi.post(`/languages/providers/${providerId}/test`),
    getTranslationTemplates: () => crmApi.get('/languages/templates'),
    createTranslationTemplate: (data) => crmApi.post('/languages/templates', data),
    updateTranslationTemplate: (id, data) => crmApi.put(`/languages/templates/${id}`, data),
    deleteTranslationTemplate: (id) => crmApi.delete(`/languages/templates/${id}`),
    getTranslationGlossary: (languageId) => crmApi.get(`/languages/${languageId}/glossary`),
    updateTranslationGlossary: (languageId, data) => crmApi.put(`/languages/${languageId}/glossary`, data),
    getTranslationContext: (key) => crmApi.get(`/languages/context/${key}`),
    updateTranslationContext: (key, data) => crmApi.put(`/languages/context/${key}`, data)
};

// Task API
export const taskAPI = {
    getTemplates: () => crmApi.get('/tasks/templates'),
    getTemplateById: (id) => crmApi.get(`/tasks/templates/${id}`),
    createTemplate: (data) => crmApi.post('/tasks/templates', data),
    updateTemplate: (id, data) => crmApi.put(`/tasks/templates/${id}`, data),
    deleteTemplate: (id) => crmApi.delete(`/tasks/templates/${id}`),
    getAll: (params) => crmApi.get('/tasks', { params }),
    getById: (id) => crmApi.get(`/tasks/${id}`),
    create: (data) => crmApi.post('/tasks', data),
    update: (id, data) => crmApi.put(`/tasks/${id}`, data),
    updateStep: (taskId, stepIndex, data) => crmApi.put(`/tasks/${taskId}/steps/${stepIndex}`, data),
    delete: (id) => crmApi.delete(`/tasks/${id}`),
    
    // Client task management
    getClientTasks: (params) => crmApi.get('/tasks/client-tasks', { params }),
    updateClientTask: (id, data) => crmApi.put(`/tasks/client-tasks/${id}`, data),
    assignTemplateToClients: (templateId, data) => crmApi.post(`/tasks/templates/${templateId}/assign`, data),
    assignTaskToClient: (data) => crmApi.post('/tasks/assign', data)
};


// Calendar API
export const calendarAPI = {
    getEvents: (params) => crmApi.get('/calendar/events', { params }),
    getById: (id) => crmApi.get(`/calendar/events/${id}`),
    create: (data) => crmApi.post('/calendar/events', data),
    update: (id, data) => crmApi.put(`/calendar/events/${id}`, data),
    delete: (id) => crmApi.delete(`/calendar/events/${id}`),
    getUpcoming: () => crmApi.get('/calendar/upcoming')
};


// Insights API
export const insightsAPI = {
    // Knowledge Base
    getKnowledgeItems: (params) => crmApi.get('/insights/knowledge', { params }),
    getKnowledgeItem: (id) => crmApi.get(`/insights/knowledge/${id}`),
    createKnowledgeItem: (data) => crmApi.post('/insights/knowledge', data),
    updateKnowledgeItem: (id, data) => crmApi.put(`/insights/knowledge/${id}`, data),
    deleteKnowledgeItem: (id) => crmApi.delete(`/insights/knowledge/${id}`),
    
    // Vendors
    getVendors: (params) => crmApi.get('/insights/vendors', { params }),
    getVendor: (id) => crmApi.get(`/insights/vendors/${id}`),
    createVendor: (data) => crmApi.post('/insights/vendors', data),
    updateVendor: (id, data) => crmApi.put(`/insights/vendors/${id}`, data),
    deleteVendor: (id) => crmApi.delete(`/insights/vendors/${id}`),
    
    // Categories & Tags
    getCategories: () => crmApi.get('/insights/categories'),
    getTags: () => crmApi.get('/insights/tags'),
    
    // Analytics
    getInsightsAnalytics: (params) => crmApi.get('/insights/analytics', { params }),
    trackView: (id, type) => crmApi.post(`/insights/${type}/${id}/view`),
    trackRating: (id, type, rating) => crmApi.post(`/insights/${type}/${id}/rating`, { rating })
};

// Opportunity API
export const opportunityAPI = {
    getAll: (params) => crmApi.get('/opportunities', { params }),
    // Get opportunities for an account
    getByAccount: (accountId) => crmApi.get(`/opportunities/account/${accountId}`),
    
    // Get opportunities for a client
    getByClient: (clientId) => crmApi.get(`/opportunities/client/${clientId}`),
    
    // CRUD operations
    create: (data) => crmApi.post('/opportunities', data),
    update: (id, data) => crmApi.put(`/opportunities/${id}`, data),
    delete: (id) => crmApi.delete(`/opportunities/${id}`),
    getById: (id) => crmApi.get(`/opportunities/${id}`)
};

// Forms API
export const formsAPI = {
    getAll: (params) => crmApi.get('/forms', { params }),
    create: (data) => crmApi.post('/forms', data),
    update: (id, data) => crmApi.put(`/forms/${id}`, data),
    delete: (id) => crmApi.delete(`/forms/${id}`),
    publish: (id) => crmApi.post(`/forms/${id}/publish`),
    getSubmissions: (id, params) => crmApi.get(`/forms/${id}/submissions`, { params }),
    exportSubmissionsCsv: (id) => crmApi.get(`/forms/${id}/submissions/export`, { responseType: 'blob' }),
    invite: (id, emails, message='') => crmApi.post(`/forms/${id}/invite`, { emails, message }),
    addWebhook: (id, webhook) => crmApi.post(`/forms/${id}/webhooks`, webhook),
    deleteWebhook: (id, webhookId) => crmApi.delete(`/forms/${id}/webhooks/${webhookId}`),
    getAnalytics: (id, params) => crmApi.get(`/forms/${id}/analytics`, { params }),
};

export const publicFormsAPI = {
    getBySlug: (slug, params) => publicApi.get(`/forms/${slug}`, { params }),
    submit: (id, payload) => publicApi.post(`/forms/${id}/submit`, payload),
    savePartial: (id, payload) => publicApi.post(`/forms/${id}/partial`, payload),
    resume: (id, token) => publicApi.get(`/forms/${id}/resume/${token}`),
    uploadFile: (id, formData) => publicApi.post(`/forms/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    })
};

// Enhanced Analytics API (for new dashboard)
export const analyticsEnhancedAPI = {
    getSalesOverview: (params) => crmApi.get('/analytics-enhanced/sales/overview', { params }),
    getServiceOverview: (params) => crmApi.get('/analytics-enhanced/service/overview', { params }),
    getMarketingOverview: (params) => crmApi.get('/analytics-enhanced/marketing/overview', { params }),
    getFinanceOverview: (params) => crmApi.get('/analytics-enhanced/finance/overview', { params })
};

// Enhanced Insights API
export const insightsEnhancedAPI = {
    getNextBestActions: (params) => crmApi.get('/insights-enhanced/next-best-actions', { params }),
    getAnomalies: (params) => crmApi.get('/insights-enhanced/anomalies', { params })
};


// Messages API
export const messagesAPI = {
    // Get messages for a conversation or between participants
    getMessages: (conversationId = null, params = {}) => {
        const url = conversationId ? `/messages/by-conversation/${conversationId}` : '/messages/by-conversation/unknown';
        return crmApi.get(url, { params });
    },
    
    // Send a new message
    sendMessage: (data) => crmApi.post('/messages', data),
    
    // Mark messages as read
    markAsRead: (messageIds) => crmApi.put('/messages/mark-read', { messageIds }),
    
    // Get unread message count
    getUnreadCount: (clientId = null) => {
        const params = clientId ? { clientId } : {};
        return crmApi.get('/messages/unread-count', { params });
    },
    
    // Get conversations list
    getConversations: (clientId = null) => {
        const params = clientId ? { clientId } : {};
        return crmApi.get('/messages/conversations', { params });
    },
    
    // Get conversation participants
    getParticipants: (conversationId) => crmApi.get(`/messages/${conversationId}/participants`),
    
    // Create a new conversation
    createConversation: (data) => crmApi.post('/messages/conversations', data),
    
    // Update conversation settings
    updateConversation: (conversationId, data) => crmApi.put(`/messages/conversations/${conversationId}`, data),
    
    // Archive conversation
    archiveConversation: (conversationId) => crmApi.post(`/messages/conversations/${conversationId}/archive`),
    
    // Get message status
    getMessageStatus: (messageId) => crmApi.get(`/messages/${messageId}/status`),
    
    // Delete message (soft delete)
    deleteMessage: (messageId) => crmApi.delete(`/messages/${messageId}`),
    
    // Upload attachment
    uploadAttachment: (formData) => crmApi.post('/messages/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    
    // Get message analytics
    getAnalytics: (params) => crmApi.get('/messages/analytics', { params })
};

export default crmApi;

